import React, { useEffect, useState } from "react";
import styled from "styled-components";
import SearchIcon from "@material-ui/icons/Search";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Input } from "@sonder/ui/source/styled/fragments/Input";
import { InputWithIcons } from "@sonder/ui/source/styled/fragments/InputWithIcons";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { Tag } from "@sonder/ui/source/styled/fragments/Tag";
import { colors } from "@sonder/ui/source/styled/theme";
import { useDebounce } from "core/hooks/useDebounce";
import {
  SearchForTaskSubjectsItem,
  useSearchForTaskSubjects,
} from "core/queries/SearchForTaskSubjects";
import {
  AdditionalFilters,
  AdditionalFiltersState,
  AdditionalFiltersTrigger,
} from "tasks/components/TaskSubjectBulkSearchAdditionalFilters";
import { SubjectTypeConfig } from "tasks/hooks/useCreateTaskFormConfig";
import { TaskContextSubjectTypeEnum } from "typings/globalGQLTypes";

const StyledError = styled.p`
  color: ${colors.redD};
`;

const Dummy = styled(Button)`
  border: 1px solid transparent;
  flex: 0 1 auto;
  height: 0;
  opacity: 0;
  pointer-events: none;
  user-select: none;
`;

const ButtonGrid = styled.div`
  box-sizing: content-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  max-height: 15rem;
  overflow-y: auto;
  padding: 0.5rem;

  ${Button} {
    flex-grow: 1;
    margin: 0 0.5rem 0.5rem 0;
  }

  ${Dummy} {
    margin: -2px 0.5rem 0 0;
  }
`;

const TagList = styled.div`
  align-items: center;
  background: ${colors.white};
  border: 1px solid ${colors.grayE};
  border-radius: 0.3rem;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.33rem 1rem;
  outline: 0;

  &:hover {
    background: ${colors.blueF};
  }

  &:active {
    background: ${colors.blue7};
    border: 1px solid ${colors.blue7};
    color: ${colors.white};
  }
`;

const TagWrapper = styled.div`
  margin: -0.5rem 0 0 -0.5rem;

  ${Tag} {
    margin: 0.5rem 0 0 0.5rem;
  }
`;

interface TaskSubjectBulkSearchProps {
  disabled?: boolean;
  onChange?: (subjects: SearchForTaskSubjectsItem[]) => void;
  subjects: SearchForTaskSubjectsItem[];
  subjectType: SubjectTypeConfig;
  parentSubject: SearchForTaskSubjectsItem | null;
}

const getFilterInputParams = (
  parentSubject: SearchForTaskSubjectsItem | null,
) => {
  const resource = parentSubject ? parentSubject.resource : null;
  switch (resource?.__typename) {
    case TaskContextSubjectTypeEnum.Building:
      return {
        buildingIds: [resource.buildingId],
        cityIds: [resource.city.id],
      };
    default:
      return null;
  }
};

const getSubjectTitle = (subject: SearchForTaskSubjectsItem) => {
  const resource = subject ? subject.resource : null;
  switch (resource?.__typename) {
    case TaskContextSubjectTypeEnum.Unit:
      return resource.aptNumberOrInternalRef;
    default:
      return null;
  }
};

const sortByTitle = (
  a: SearchForTaskSubjectsItem,
  b: SearchForTaskSubjectsItem,
) => {
  return (getSubjectTitle(a) || "") < (getSubjectTitle(b) || "") ? -1 : 1;
};

const fillDummies = (title: string) =>
  new Array(10)
    .fill(undefined)
    /* eslint-disable-next-line react/no-array-index-key */
    .map((v, k) => <Dummy key={`dummy-${k}`}>{title}</Dummy>);

const initialAdditionalFiltersState = {
  unit: { live: true },
};

interface TaskSubjectBulkSearchResults {
  subjects: SearchForTaskSubjectsItem[];
}

export const TaskSubjectBulkSearchResults = ({
  subjects,
}: TaskSubjectBulkSearchResults) => (
  <TagWrapper>
    {subjects.sort(sortByTitle).map(s => (
      <Tag key={s.id}>{getSubjectTitle(s)}</Tag>
    ))}
  </TagWrapper>
);

export const TaskSubjectBulkSearch = ({
  disabled,
  onChange,
  parentSubject,
  subjects,
  subjectType,
}: TaskSubjectBulkSearchProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(false);
  const [additionalFilters, setAdditionalFilters] =
    useState<AdditionalFiltersState>(initialAdditionalFiltersState);
  const { bulkTaskConfig, resultType } = subjectType;
  const { searchPlaceholder = "Search subjects..." } = bulkTaskConfig || {};

  // Clear search term and selected when resultType changes.
  useEffect(() => {
    setSearchTerm("");
  }, [subjectType]);

  const filterInputParams = getFilterInputParams(parentSubject);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const filterInput = {
    unit: {
      ...filterInputParams,
      live: additionalFilters.unit?.live,
    },
  };

  const { data, loading, error } = useSearchForTaskSubjects({
    skip: !resultType || !parentSubject,
    variables: {
      limit: 500,
      filterInput,
      searchTerm: debouncedSearchTerm,
      resultTypes: [resultType],
    },
  });
  const items = data?.searchWithMetadata.items.sort(sortByTitle) || [];
  const subjectIds = subjects.map(subject => subject.id);

  const isEmpty = items.length === 0;
  const isSearchTermEmpty = debouncedSearchTerm === "";
  const isEmptyAndLoading = isEmpty && loading;
  const isEmptyAndSearched = !loading && isEmpty && !isSearchTermEmpty;

  const dummyTitle = (items && items[0] && getSubjectTitle(items[0])) || "";

  return (
    <Dropdown
      trigger={
        subjects.length > 0 ? (
          <TagList>
            <TaskSubjectBulkSearchResults subjects={subjects} />
          </TagList>
        ) : (
          <Button
            fullWidth
            iconRight
            disabled={disabled || !resultType}
            type="button"
          >
            {searchPlaceholder}
            <ChevronDown />
          </Button>
        )
      }
    >
      {({ isOpen: isPortalOpen }) =>
        isPortalOpen && (
          <FilterDropdownContent lockMaxWidth align="left">
            <InputWithIcons childLeft childRight>
              <SearchIcon />
              <Input
                autoFocus
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <AdditionalFiltersTrigger
                isOpen={additionalFiltersOpen}
                onClick={() => setAdditionalFiltersOpen(!additionalFiltersOpen)}
              />
            </InputWithIcons>
            <AdditionalFilters
              additionalFilters={additionalFilters}
              isOpen={additionalFiltersOpen}
              subjectType={subjectType.subjectType}
              onChange={newAdditionalFilters =>
                setAdditionalFilters(newAdditionalFilters)
              }
            />
            {error && error.message && (
              <StyledError>There was an error: {error.message}</StyledError>
            )}
            {isEmptyAndLoading && <FilterEmpty>Searching...</FilterEmpty>}
            {isEmptyAndSearched && (
              <FilterEmpty>{`No Results for ${searchTerm}.`}</FilterEmpty>
            )}
            {!isEmpty && !isEmptyAndSearched && (
              <ButtonGrid>
                {items.map(item => {
                  const selected = subjectIds.includes(item.id);
                  return (
                    <Button
                      key={item.id}
                      selected={selected}
                      onClick={() => {
                        if (onChange)
                          onChange(
                            selected
                              ? subjects.filter(s => s.id !== item.id)
                              : [...subjects, item],
                          );
                      }}
                    >
                      {getSubjectTitle(item)}
                    </Button>
                  );
                })}
                {fillDummies(dummyTitle)}
              </ButtonGrid>
            )}
            <FilterFooter>
              {`${subjectIds.length || 0} Selected`}
              <Link
                onClick={() => {
                  requestAnimationFrame(() => {
                    if (onChange) onChange(subjectIds.length > 0 ? [] : items);
                  });
                }}
              >
                {subjectIds.length > 0 ? "Clear selected" : "Select all"}
              </Link>
            </FilterFooter>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
